import React, { useState } from "react";
import { CheckCircle2, XCircle, AlertCircle, MessageSquare } from "lucide-react";

export default function ATTDecisionPanel({ onSubmit, initialNotes = "", currentStatus = "pending" }) {
    const [notes, setNotes] = useState(initialNotes);
    const [loading, setLoading] = useState(false);

    const handleAction = async (status) => {
        if (loading) return;
        setLoading(true);
        try {
            await onSubmit({ status, notes });
        } catch (err) {
            console.error("Action failed:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full">
            <div className="p-5 border-b border-slate-100 bg-slate-50/50">
                <h4 className="font-bold text-navy-900 text-sm flex items-center gap-2">
                    <CheckCircle2 size={18} className="text-emerald-500" /> Auditor Decision Panel
                </h4>
            </div>

            <div className="p-6 flex-1 space-y-6 overflow-y-auto">
                {/* Decision Buttons */}
                <div className="space-y-3">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Select Audit Action</p>
                    <div className="grid grid-cols-1 gap-2">
                        <button
                            onClick={() => handleAction("approved")}
                            disabled={loading}
                            className={`flex items-center justify-between p-4 rounded-xl border transition-all ${currentStatus === "approved"
                                    ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                                    : "bg-white border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/30 text-slate-600"
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                <CheckCircle2 size={20} className={currentStatus === "approved" ? "text-emerald-500" : "text-slate-400"} />
                                <span className="text-xs font-bold">Approve Document</span>
                            </div>
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        </button>

                        <button
                            onClick={() => handleAction("needs_fix")}
                            disabled={loading}
                            className={`flex items-center justify-between p-4 rounded-xl border transition-all ${currentStatus === "needs_fix"
                                    ? "bg-orange-50 border-orange-500 text-orange-700"
                                    : "bg-white border-slate-200 hover:border-orange-500 hover:bg-orange-50/30 text-slate-600"
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                <AlertCircle size={20} className={currentStatus === "needs_fix" ? "text-orange-500" : "text-slate-400"} />
                                <span className="text-xs font-bold">Request Correction</span>
                            </div>
                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                        </button>

                        <button
                            onClick={() => handleAction("rejected")}
                            disabled={loading}
                            className={`flex items-center justify-between p-4 rounded-xl border transition-all ${currentStatus === "rejected"
                                    ? "bg-red-50 border-red-500 text-red-700"
                                    : "bg-white border-slate-200 hover:border-red-500 hover:bg-red-50/30 text-slate-600"
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                <XCircle size={20} className={currentStatus === "rejected" ? "text-red-500" : "text-slate-400"} />
                                <span className="text-xs font-bold">Reject Document</span>
                            </div>
                            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        </button>
                    </div>
                </div>

                {/* Notes Section */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Feedback to Client</label>
                        <MessageSquare size={14} className="text-slate-300" />
                    </div>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Provide details for the client (e.g., 'Please ensure the expiry date is clearly visible.')"
                        className="w-full h-32 p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900 outline-none transition-all resize-none italic"
                    />
                </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 italic text-[9px] text-slate-400 text-center">
                {loading ? "Processing auditor decision..." : "Audited decisions are logged permanently in the ATT ledger."}
            </div>
        </div>
    );
}
